import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Truck, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as authApi from '@/services/authApi';

const ShippedTransfers = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [shippedTransfers, setShippedTransfers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (user) {
      fetchShippedTransfers();
    }
  }, [user]);

  const fetchUser = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/login');
    }
  };

  const fetchShippedTransfers = async () => {
    setIsLoading(true);
    try {
      const userSection = user?.sections?.[0];
      
      if (userSection && userSection !== 'FOB') {
        const response = await apiClient.get('/internal-transfer/history', {
          params: { 
            status: 'shipped',
            section: userSection
          }
        });
        setShippedTransfers(response.data.transfers || []);
      }
    } catch (error) {
      console.error('Error fetching shipped transfers:', error);
      toast.error('Failed to load shipped transfers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToBundleReceipt = (transfer) => {
    // Navigate to the section-specific bundle receipt page
    const sectionName = transfer.to_section.toLowerCase();
    navigate(`/sections/${sectionName}/bundle-receipt`, {
      state: {
        transferId: transfer.transfer_id,
        documentNumber: transfer.document_number,
        bundleCodes: transfer.bundle_codes
      }
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/dashboard')} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Shipped Transfers</h1>
              <p className="text-sm text-[#9CA3AF]">Incoming transfers to your section</p>
            </div>
          </div>
          <Button onClick={fetchShippedTransfers} variant="secondary" size="sm">
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-green-500" />
              Incoming Transfers
            </CardTitle>
            <CardDescription>
              Transfers shipped from other sections awaiting bundle receipt
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : shippedTransfers.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Package className="w-16 h-16 mx-auto mb-3 opacity-20" />
                <p>No incoming transfers</p>
              </div>
            ) : (
              <div className="space-y-4">
                {shippedTransfers.map((transfer) => (
                  <div
                    key={transfer.transfer_id}
                    className="border border-[#E5DFD0] rounded-lg p-4 hover:bg-[#FFFBF5] transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{transfer.document_number}</h3>
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                            Shipped
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                          <div>
                            <span className="text-gray-500">From:</span>{' '}
                            <strong>{transfer.from_section}</strong>
                          </div>
                          <div>
                            <span className="text-gray-500">Bundles:</span>{' '}
                            <strong>{transfer.bundle_count}</strong>
                          </div>
                          <div>
                            <span className="text-gray-500">Shipped by:</span>{' '}
                            <strong>{transfer.shipped_by_name}</strong>
                          </div>
                          <div>
                            <span className="text-gray-500">Shipped at:</span>{' '}
                            <strong>{formatDate(transfer.shipped_at)}</strong>
                          </div>
                        </div>
                        <details className="text-sm">
                          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                            View Bundles ({transfer.bundle_count})
                          </summary>
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
                            {transfer.bundle_codes?.map((code, idx) => (
                              <div key={code} className="font-mono text-xs py-1">
                                {idx + 1}. {code}
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                      <Button
                        onClick={() => handleNavigateToBundleReceipt(transfer)}
                        className="bg-blue-600 hover:bg-blue-700 ml-4"
                      >
                        Proceed to Bundle Receipt
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ShippedTransfers;
